import { Money } from "@hubrise/react-components"

import { ICatalog, ISku } from "../models"
import { Option, OptionList } from "../utils"

type ItemOption = {
  optionListName: string
  name: string
}

class ItemCustomisation {
  private readonly catalog: ICatalog
  private sku: ISku
  private options: Array<ItemOption>

  constructor(catalog: ICatalog, sku: ISku) {
    this.catalog = catalog
    this.sku = sku
    this.options = []
    this.onSkuChange()
  }

  optionNames(optionListName: string): Array<string> {
    return this.options.filter(({ optionListName: name }) => name === optionListName).map(({ name }) => name)
  }

  setOptionNames(optionListName: string, optionNames: Array<string>): void {
    this.options = this.options
      .filter((option) => option.optionListName !== optionListName)
      .concat(optionNames.map((name) => ({ optionListName: optionListName, name })))
  }

  setSku(sku: ISku): void {
    this.sku = sku
    this.onSkuChange()
  }

  price(variantRef: string | null): Money {
    return this.options.reduce((acc, { name: optionName, optionListName }) => {
      const optionList = OptionList.bySku(this.catalog, this.sku).find((ol) => ol.name === optionListName)
      if (!optionList) return acc

      const option = optionList.options.find((o) => o.name === optionName)
      if (!option) return acc

      return acc.plus(Option.price(option, variantRef))
    }, this.nullMoney)
  }

  /**
   * Adjust the options after a sku change:
   * - Remove the options that are not valid for the new sku
   * - Add default options for single choice option lists with no option selected
   */
  private onSkuChange(): void {
    const optionLists = OptionList.bySku(this.catalog, this.sku)
    this.options = this.options
      // Remove options which no longer exist
      .filter((option) => {
        const optionList = optionLists.find((ol) => ol.name === option.optionListName)
        return optionList !== undefined && optionList.options.find((o) => o.name === option.name) !== undefined
      })

    // Add default options
    optionLists
      .filter((optionList) => OptionList.isSingleChoice(optionList))
      .forEach((optionList) => {
        if (this.optionNames(optionList.name).length === 0)
          this.options.push({ optionListName: optionList.name, name: optionList.options[0].name })
      })
  }

  private get nullMoney(): Money {
    return Money.zero(this.catalog.currency)
  }
}

export default ItemCustomisation
